import { useEffect, useRef, useId, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, CircleAlert, Info, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'

export function WorkflowLayout({
  title,
  description,
  backTo = '/admin/show-transfer',
  backLabel = 'Import & export',
  children,
}: {
  title: string
  description: string
  backTo?: string
  backLabel?: string
  children: ReactNode
}) {
  return (
    <div className="mx-auto w-full max-w-3xl space-y-7 pb-8 [&_fieldset]:min-w-0 [&_button]:h-auto [&_button]:min-h-9 [&_button]:max-w-full [&_button]:whitespace-normal">
      <header className="space-y-5">
        <Link
          to={backTo}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {backLabel}
        </Link>
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="text-muted-foreground">{description}</p>
        </div>
      </header>
      {children}
    </div>
  )
}

export function WorkflowHeading({
  children,
  description,
}: {
  children: ReactNode
  description?: ReactNode
}) {
  const ref = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    ref.current?.focus()
  }, [children])
  return (
    <div className="space-y-2">
      <h2
        ref={ref}
        tabIndex={-1}
        className="text-xl font-semibold tracking-tight focus:outline-none"
      >
        {children}
      </h2>
      {description && <p className="text-muted-foreground">{description}</p>}
    </div>
  )
}

export function WorkflowFooter({
  secondary,
  children,
}: {
  secondary?: ReactNode
  children?: ReactNode
}) {
  return (
    <div
      className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t pt-5"
      data-workflow-footer
    >
      <div className="flex min-w-0 max-w-full flex-wrap items-center gap-3">{secondary}</div>
      <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-3">
        {children}
      </div>
    </div>
  )
}

export function StatusNotice({
  tone = 'info',
  title,
  children,
  className,
  role,
}: {
  tone?: 'info' | 'warning' | 'error' | 'success'
  title?: string
  children?: ReactNode
  className?: string
  role?: 'alert' | 'status'
}) {
  const Icon = { info: Info, warning: TriangleAlert, error: CircleAlert, success: CheckCircle2 }[
    tone
  ]
  return (
    <div
      role={role ?? (tone === 'error' ? 'alert' : undefined)}
      className={cn(
        'flex min-w-0 items-start gap-3 rounded-md border-l-4 p-4 text-sm',
        {
          info: 'border-border bg-muted text-foreground',
          warning: 'border-warning bg-warning/10 text-warning-foreground',
          error: 'border-destructive bg-destructive/10 text-error-foreground',
          success: 'border-success bg-success/10 text-success-foreground',
        }[tone],
        className
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 space-y-2 break-words">
        {title && <p className="font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  )
}

export function PrivacyOptions({
  value,
  onChange,
  disabled,
  legend = 'Personal information in the file',
}: {
  value: 'limited' | 'full-contact'
  onChange: (value: 'limited' | 'full-contact') => void
  disabled?: boolean
  legend?: string
}) {
  const name = useId()
  return (
    <fieldset disabled={disabled} className="space-y-3">
      <legend className="mb-2 text-sm font-semibold">{legend}</legend>
      {(
        [
          [
            'limited',
            'Names and show records',
            'Show setup, entries, results, names and operational comments. Contact details are excluded.',
          ],
          [
            'full-contact',
            'Names, show records and contact details',
            'Show setup, entries, results, names, operational comments and stored contact details.',
          ],
        ] as const
      ).map(([id, label, description]) => (
        <label
          key={id}
          className={cn(
            'flex cursor-pointer items-start gap-3 rounded-lg border p-4 has-[:disabled]:cursor-default has-[:disabled]:opacity-60',
            value === id && 'border-primary bg-muted/50'
          )}
        >
          <input
            type="radio"
            name={name}
            checked={value === id}
            onChange={() => onChange(id)}
            className="mt-1 accent-primary"
          />
          <span>
            <span className="block text-sm font-medium">{label}</span>
            <span className="mt-1 block text-sm text-muted-foreground">{description}</span>
          </span>
        </label>
      ))}
    </fieldset>
  )
}
